import { z } from 'zod/v4'

import { BroadcastStatus } from '../../contracts/notification-management.types.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «Avisos»: one page of broadcasts, one page of the delivery log and the active channel catalogue in one answer. Rows
 * travel field by field with what the page paints: a broadcast does NOT carry its body, its target criteria nor its
 * internal error; a log row carries the error of ITS delivery (the table paints it) but neither the template nor its
 * creation date.
 */

/** The size of the page of both tables (the page of the old routes). */
export const ADMIN_BROADCASTS_PAGE_SIZE = 20

export const ADMIN_DELIVERY_LOG_STATUSES = ['sent', 'failed', 'pending', 'cancelled'] as const

export const adminBroadcastRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  status: z.enum(BroadcastStatus),
  channels: z.array(z.string()),
  totalRecipients: z.number(),
  sentCount: z.number(),
  failedCount: z.number(),
  skippedCount: z.number(),
})

export const adminDeliveryLogRowSchema = z.strictObject({
  id: z.string(),
  /** What the table paints when the name is missing. */
  tenantId: z.string(),
  tenantName: z.string().nullable(),
  /** What the table paints when the e-mail is missing. */
  userId: z.string().nullable(),
  /** Who RECEIVED it (`notifications.recipient`, or the e-mail of the user): it is painted today. */
  userEmail: z.string().nullable(),
  channel: z.string(),
  status: z.enum(ADMIN_DELIVERY_LOG_STATUSES),
  sentAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
})

export const adminBroadcastChannelOptionSchema = z.strictObject({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number(),
})

/** The three states the status selector offers; the channel is text because the catalogue lives in the database. */
export const adminBroadcastsQuerySchema = z.strictObject({
  page: z.coerce.number<number>().int().min(1).default(1),
  logPage: z.coerce.number<number>().int().min(1).default(1),
  logChannel: z.string().min(1).max(50).optional(),
  logStatus: z.enum(['sent', 'failed', 'pending']).optional(),
  logSearch: z.string().min(1).max(200).optional(),
})

export const adminBroadcastsScreen = defineScreen({
  id: 'admin.broadcasts',
  path: '/api/screens/admin/broadcasts',
  access: 'super_admin',
  query: adminBroadcastsQuerySchema,
  response: z.strictObject({
    broadcasts: screenSection(
      z.strictObject({
        items: z.array(adminBroadcastRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    deliveryLog: screenSection(
      z.strictObject({
        items: z.array(adminDeliveryLogRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    channels: screenSection(z.array(adminBroadcastChannelOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.broadcasts'],
  budget: { requests: 1, statements: 15, bytes: 120_000, p95Ms: 400 },
  summary:
    'Announcements: one page of broadcasts, one page of the delivery log with its filters and the active channel catalogue',
})

export type AdminBroadcastRow = z.infer<typeof adminBroadcastRowSchema>
export type AdminDeliveryLogRow = z.infer<typeof adminDeliveryLogRowSchema>
export type AdminBroadcastChannelOption = z.infer<typeof adminBroadcastChannelOptionSchema>
export type AdminBroadcastsQuery = ScreenQuery<typeof adminBroadcastsScreen>
export type AdminBroadcastsData = ScreenData<typeof adminBroadcastsScreen>
